// Обновление мобильных приложений: напоминание и принудительный гейт.
//
// Ключ — app_id, а не партнёр: сборка в сторе одна на приложение, а конфигов
// у нас по одному на партнёра. Политика, положенная в партнёрский конфиг,
// закрыла бы только его жителей, и человек был бы уверен, что закрыл всех.
//
// Управление здесь, а не в партнёрском кабинете, потому что принудительное
// обновление — следствие нашего ломающего изменения или битой сборки, то есть
// решение платформы. Партнёр не собирал этот бинарник.
//
// ВАЖНО: сервер не отправляет текст «обновитесь». Он отдаёт число — «актуален
// билд N» — а сравнивает и рисует шит само приложение. Значит выставленное
// здесь увидят только сборки, в которых уже есть приёмник; на установленных
// сегодня не изменится ничего.

import { useEffect, useState } from "react";
import { gql } from "../lib/gql";

type Policy = {
  appId: string;
  androidMinSupportedBuild: number | null;
  iosMinSupportedBuild: number | null;
  androidLatestBuild: number | null;
  iosLatestBuild: number | null;
  message: string | null;
  androidStoreUrl: string | null;
  iosStoreUrl: string | null;
  sessionsWithBuild: number;
  sessionsTotal: number;
};

const LIST_QUERY = `
  query AdminAppUpdatePolicies {
    adminAppUpdatePolicies {
      appId
      androidMinSupportedBuild
      iosMinSupportedBuild
      androidLatestBuild
      iosLatestBuild
      message
      androidStoreUrl
      iosStoreUrl
      sessionsWithBuild
      sessionsTotal
    }
  }
`;

const SET_MUTATION = `
  mutation AdminSetAppUpdatePolicy($input: AdminAppUpdatePolicyInput!) {
    adminSetAppUpdatePolicy(input: $input) {
      appId
      androidMinSupportedBuild
      iosMinSupportedBuild
      androidLatestBuild
      iosLatestBuild
      message
      androidStoreUrl
      iosStoreUrl
      sessionsWithBuild
      sessionsTotal
    }
  }
`;

type Draft = Record<string, Record<string, string>>;

const NUM_FIELDS = [
  ["androidLatestBuild", "Android: последний билд (versionCode из Play Console)"],
  ["iosLatestBuild", "iOS: последний билд (Build из App Store Connect)"],
  ["androidMinSupportedBuild", "Android: минимальный билд (versionCode)"],
  ["iosMinSupportedBuild", "iOS: минимальный билд (Build)"],
] as const;

function toDraft(p: Policy): Record<string, string> {
  return {
    androidLatestBuild: p.androidLatestBuild?.toString() ?? "",
    iosLatestBuild: p.iosLatestBuild?.toString() ?? "",
    androidMinSupportedBuild: p.androidMinSupportedBuild?.toString() ?? "",
    iosMinSupportedBuild: p.iosMinSupportedBuild?.toString() ?? "",
    message: p.message ?? "",
    androidStoreUrl: p.androidStoreUrl ?? "",
    iosStoreUrl: p.iosStoreUrl ?? "",
  };
}

export function AppUpdatePage() {
  const [policies, setPolicies] = useState<Policy[]>([]);
  const [draft, setDraft] = useState<Draft>({});
  const [savingId, setSavingId] = useState<string | null>(null);
  const [savedId, setSavedId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  async function load() {
    try {
      const data = await gql<{ adminAppUpdatePolicies: Policy[] }>(LIST_QUERY);
      setPolicies(data.adminAppUpdatePolicies);
      setDraft(Object.fromEntries(data.adminAppUpdatePolicies.map((p) => [p.appId, toDraft(p)])));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoaded(true);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function save(p: Policy) {
    setSavingId(p.appId);
    setSavedId(null);
    setError(null);
    const d = draft[p.appId] ?? {};
    // Пустое поле — это «уровень выключен», а не ноль. Отправляем null.
    const num = (k: string) => (d[k]?.trim() ? Number(d[k]) : null);
    try {
      const data = await gql<{ adminSetAppUpdatePolicy: Policy }>(SET_MUTATION, {
        input: {
          appId: p.appId,
          androidLatestBuild: num("androidLatestBuild"),
          iosLatestBuild: num("iosLatestBuild"),
          androidMinSupportedBuild: num("androidMinSupportedBuild"),
          iosMinSupportedBuild: num("iosMinSupportedBuild"),
          message: d.message?.trim() || null,
          androidStoreUrl: d.androidStoreUrl?.trim() || null,
          iosStoreUrl: d.iosStoreUrl?.trim() || null,
        },
      });
      const saved = data.adminSetAppUpdatePolicy;
      setPolicies((prev) => prev.map((x) => (x.appId === saved.appId ? saved : x)));
      setDraft((cur) => ({ ...cur, [saved.appId]: toDraft(saved) }));
      setSavedId(p.appId);
      setTimeout(() => setSavedId((cur) => (cur === p.appId ? null : cur)), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSavingId(null);
    }
  }

  return (
    <div className="page">
      <h2>Обновление приложений</h2>
      <p style={{ color: "var(--muted, #888)", fontSize: 14, maxWidth: 760 }}>
        Сервер отдаёт приложению номер актуального билда, а сравнивает и
        показывает сообщение само приложение. Поэтому выставленное здесь
        увидят только сборки, в которых уже есть приёмник — на установленных
        сегодня не изменится ничего.
      </p>
      <p style={{ color: "var(--muted, #888)", fontSize: 14, maxWidth: 760 }}>
        Числа берутся из консолей: Android — <b>versionCode</b> из Play
        Console (порядка 29 700 000), iOS — <b>Build</b> из App Store Connect
        (порядка 200). Системы нумерации разные, и число одной платформы в
        поле другой заперло бы всех — сервер такое отклонит.
      </p>
      <p style={{ color: "var(--muted, #888)", fontSize: 14, maxWidth: 760 }}>
        <b>Последний билд</b> — закрываемое напоминание «вышла новая версия».
        Ничего не блокирует. <b>Минимальный билд</b> — блокирующий экран: всё,
        что ниже, в приложение не войдёт. Пустое поле = уровень выключен.
      </p>
      {error && <div className="error">{error}</div>}
      {!loaded ? (
        <div>Загрузка…</div>
      ) : (
        policies.map((p) => {
          const d = draft[p.appId] ?? {};
          const canGate = p.sessionsWithBuild > 0;
          const dirty = JSON.stringify(d) !== JSON.stringify(toDraft(p));
          return (
            <div key={p.appId} className="card" style={{ marginBottom: 24, maxWidth: 760 }}>
              <h3 style={{ marginBottom: 4 }}>{p.appId}</h3>
              <div style={{ color: "var(--muted, #888)", fontSize: 13, marginBottom: 12 }}>
                Живых сессий: {p.sessionsTotal}. Присылают свой билд:{" "}
                {p.sessionsWithBuild}.
              </div>
              {!canGate && (
                <div
                  style={{
                    background: "rgba(245, 158, 11, 0.12)",
                    border: "1px solid rgba(245, 158, 11, 0.4)",
                    borderRadius: 8,
                    padding: "8px 12px",
                    fontSize: 13,
                    marginBottom: 12,
                  }}
                >
                  Минимальный билд задать нельзя: ни один клиент не присылает
                  свою версию, распределение неизвестно — гейт заперёт всех.
                  Напоминание доступно.
                </div>
              )}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                {NUM_FIELDS.map(([key, label]) => {
                  const isMin = key.includes("Min");
                  return (
                    <label key={key} style={{ fontSize: 13 }}>
                      {label}
                      <input
                        type="number"
                        value={d[key] ?? ""}
                        disabled={isMin && !canGate}
                        onChange={(e) =>
                          setDraft((cur) => ({
                            ...cur,
                            [p.appId]: { ...cur[p.appId], [key]: e.target.value },
                          }))
                        }
                        placeholder="—"
                        style={{ width: "100%", padding: "6px 8px", marginTop: 4 }}
                      />
                    </label>
                  );
                })}
              </div>
              <label style={{ fontSize: 13, display: "block", marginTop: 12 }}>
                Текст блокирующего экрана (пусто — дефолт приложения)
                <input
                  type="text"
                  value={d.message ?? ""}
                  onChange={(e) =>
                    setDraft((cur) => ({
                      ...cur,
                      [p.appId]: { ...cur[p.appId], message: e.target.value },
                    }))
                  }
                  style={{ width: "100%", padding: "6px 8px", marginTop: 4 }}
                />
              </label>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 12 }}>
                <label style={{ fontSize: 13 }}>
                  Ссылка Play (пусто — дефолт)
                  <input
                    type="url"
                    value={d.androidStoreUrl ?? ""}
                    onChange={(e) =>
                      setDraft((cur) => ({
                        ...cur,
                        [p.appId]: { ...cur[p.appId], androidStoreUrl: e.target.value },
                      }))
                    }
                    style={{ width: "100%", padding: "6px 8px", marginTop: 4 }}
                  />
                </label>
                <label style={{ fontSize: 13 }}>
                  Ссылка App Store (пусто — дефолт)
                  <input
                    type="url"
                    value={d.iosStoreUrl ?? ""}
                    onChange={(e) =>
                      setDraft((cur) => ({
                        ...cur,
                        [p.appId]: { ...cur[p.appId], iosStoreUrl: e.target.value },
                      }))
                    }
                    style={{ width: "100%", padding: "6px 8px", marginTop: 4 }}
                  />
                </label>
              </div>
              <div style={{ marginTop: 14 }}>
                <button onClick={() => save(p)} disabled={savingId === p.appId || !dirty}>
                  {savingId === p.appId ? "…" : "Сохранить"}
                </button>
                {savedId === p.appId && (
                  <span style={{ color: "green", fontSize: 13, marginLeft: 8 }}>✓</span>
                )}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}
